import classNames from "classnames";
import { memo, ReactNode } from "react";
import { useSettingSectionWrapperContext } from "./setting-section-wrapper-context";
import * as css from "./setting-wrapper.module.css";
import { useHighlightTarget } from "./use-highlight-target";

export interface SettingWrapperProps {
  /** Setting id matched against the `?setting` query param. */
  id: string;
  children: ReactNode;
  className?: string;
  /**
   * Use the compact (tight) highlight style. Defaults to the enclosing group's
   * `compact` value, so settings inside a compact group are compact too.
   */
  compact?: boolean;
  /** Highlight only, never scroll into view. */
  disableAutoScroll?: boolean;
  /** Override the default `{ behavior: "smooth", block: "center" }`. */
  scrollOptions?: ScrollIntoViewOptions;
}

/**
 * Wraps an individual setting. When rendered inside a `<SettingSectionWrapper>` it only
 * matches when BOTH `?group` and `?setting` match; standalone (outside any
 * group) it matches on `?setting` alone.
 */
export const SettingWrapper = memo(function SettingWrapper({
  id,
  children,
  className,
  compact,
  disableAutoScroll,
  scrollOptions,
}: SettingWrapperProps) {
  const group = useSettingSectionWrapperContext();
  const { ref, isActive, isPulsing, onAnimationEnd } = useHighlightTarget(
    { kind: "setting", id, groupId: group?.groupId },
    { disableAutoScroll, scrollOptions },
  );

  const isCompact = compact ?? group?.compact ?? false;

  return (
    <div
      className={classNames(
        css.wrapper,
        isActive && css.active,
        isActive && isPulsing && css.pulse,
        isCompact && css.compact,
        className,
      )}
      onAnimationEnd={onAnimationEnd}
      ref={ref}
    >
      {children}
    </div>
  );
});
